// Phase 3B — device-local path memory.
//
// First-party localStorage only. No profiling, no third-party identity, no
// personal data: only content references the visitor already interacted with,
// plus counters used to decide when a prompt has been earned.

import {
  mergeMemory,
  type MemoryKind,
  type PathMemoryEntry,
} from "./return-loop";

const MEM_KEY = "ka.path.memory.v1";
const STATS_KEY = "ka.path.stats.v1";
const EVENT = "ka:path-memory";

export interface PathStats {
  /** ISO yyyy-mm-dd days the visitor has been seen (bounded). */
  days: string[];
  startedCount: number;
  valueCount: number;
  lastPromptAt: number | null;
  lastDismissedAt: number | null;
}

const EMPTY_STATS: PathStats = {
  days: [],
  startedCount: 0,
  valueCount: 0,
  lastPromptAt: null,
  lastDismissedAt: null,
};

function safeRead<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeWrite(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {
    /* noop */
  }
}

export function readMemory(): PathMemoryEntry[] {
  const list = safeRead<PathMemoryEntry[]>(MEM_KEY, []);
  return Array.isArray(list) ? list : [];
}

export function readStats(): PathStats {
  const s = safeRead<PathStats>(STATS_KEY, EMPTY_STATS);
  return { ...EMPTY_STATS, ...s, days: Array.isArray(s?.days) ? s.days : [] };
}

function writeStats(next: PathStats): void {
  safeWrite(STATS_KEY, next);
}

/** Records an interaction with a piece of content. */
export function rememberPath(entry: {
  kind: MemoryKind;
  ref: string;
  title: string;
  href: string;
  progress?: number;
  completed?: boolean;
}): void {
  if (typeof window === "undefined") return;
  safeWrite(MEM_KEY, mergeMemory(readMemory(), { ...entry, at: Date.now() }));
}

/** Increments the earned-value counters. */
export function recordAction(kind: "start" | "value"): void {
  if (typeof window === "undefined") return;
  const s = readStats();
  writeStats({
    ...s,
    startedCount: s.startedCount + (kind === "start" ? 1 : 0),
    valueCount: s.valueCount + (kind === "value" ? 1 : 0),
  });
}

/** Marks today as seen. Returns the number of distinct days seen. */
export function markVisitDay(now = new Date()): number {
  if (typeof window === "undefined") return 1;
  const today = now.toISOString().slice(0, 10);
  const s = readStats();
  if (s.days.includes(today)) return s.days.length;
  const days = [...s.days, today].slice(-90);
  writeStats({ ...s, days });
  return days.length;
}

export function recordPromptShown(now = Date.now()): void {
  writeStats({ ...readStats(), lastPromptAt: now });
}

export function recordPromptDismissed(now = Date.now()): void {
  writeStats({ ...readStats(), lastDismissedAt: now });
}

export function subscribeMemory(fn: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(EVENT, fn);
  window.addEventListener("storage", fn);
  return () => {
    window.removeEventListener(EVENT, fn);
    window.removeEventListener("storage", fn);
  };
}

/** Clears everything this module stores. Used by the privacy control. */
export function forgetPath(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(MEM_KEY);
    window.localStorage.removeItem(STATS_KEY);
  } catch {
    /* private mode */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {
    /* noop */
  }
}
